import React, { createContext, useContext, useEffect } from "react";
import { boosterData, getUserBoosters } from "../../services/BoosterServices";
import { useFetchedUserContext } from "./FetchedUser";
import {
  usePaginatedData,
  UsePaginatedDataResult,
} from "../helper/usePaginatedData";

interface BoosterContextProps {
  boostersState: UsePaginatedDataResult<boosterData>;
}

const FetchedUserBoosterContext = createContext<
  BoosterContextProps | undefined
>(undefined);

export const FetchedUserBoosterProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const boostersState = usePaginatedData<boosterData>();
  const limit = 8;
  const { user } = useFetchedUserContext();

  useEffect(() => {
    const fetchBoosters = async (userId: number) => {
      if (boostersState.loading || !boostersState.hasMore) return;

      boostersState.setLoading(true);
      boostersState.setError(null);
      try {
        const offset = (boostersState.page - 1) * limit;
        const fetchedBoosters = await getUserBoosters(limit, offset, userId);

        if (fetchedBoosters.length < limit) {
          boostersState.setHasMore(false);
        }

        boostersState.setData((prevBoosters) => {
          const newBoosters = fetchedBoosters.filter(
            (fetchedBooster) =>
              !prevBoosters.some(
                (prevBooster) => prevBooster.id === fetchedBooster.id
              )
          );
          return [...prevBoosters, ...newBoosters];
        });

        boostersState.setFilteredData((prevBoosters) => {
          const newBoosters = fetchedBoosters.filter(
            (fetchedBooster) =>
              !prevBoosters.some(
                (prevBooster) => prevBooster.id === fetchedBooster.id
              )
          );
          return [...prevBoosters, ...newBoosters];
        });
      } catch (err) {
        boostersState.setError("Failed to fetch boosters");
      } finally {
        boostersState.setLoading(false);
      }
    };

    if (user && boostersState.page > 0) fetchBoosters(user.id);

    // only rerun on page change ...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boostersState.page]);

  useEffect(() => {
    boostersState.setPage(0);
    boostersState.setHasMore(true);
    boostersState.setData([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <FetchedUserBoosterContext.Provider
      value={{
        boostersState,
      }}
    >
      {children}
    </FetchedUserBoosterContext.Provider>
  );
};

export const useFetchedUserBoostersContext = () => {
  const context = useContext(FetchedUserBoosterContext);
  if (!context) {
    throw new Error("useBoosterContext must be used within a BoosterProvider");
  }
  return context;
};
